import {Component, OnDestroy, OnInit} from '@angular/core';
import {RegionService} from '../../../services/region-service';
import {PlaceService} from '../../../services/place.service';
import {ActivatedRoute} from '@angular/router';
import {AuthService} from '../../../services/auth.service';
import {Place} from '../../../models/place';
import {combineLatest, finalize, firstValueFrom, forkJoin, from, map, Subject, takeUntil} from 'rxjs';
import {Region} from '../../../models/region';
import {DatePipe, NgForOf, NgIf} from '@angular/common';
import {PlaceBoxComponent} from '../place-box/place-box.component';
import {FormsModule} from '@angular/forms';
import {FilterService} from '../../../services/filter.service';
import {MapService} from '../../../services/map.service';
import {PlaceListSearchComponent} from './place-list-search/place-list-search.component';
import {PlaceListDetailComponent} from './place-list-detail/place-list-detail.component';
import {GeneralService} from '../../../services/general.service';
import {PlaceNominateBoxComponent} from '../place-nominate-box/place-nominate-box.component';
import {PlaceListEmptyComponent} from './place-list-empty/place-list-empty.component';
import {PlaceListFilterComponent} from './place-list-filter/place-list-filter.component';
import {ScheduleService} from '../../../services/schedule.service';
import {User} from '../../../models/user';

@Component({
  selector: 'app-place-list',
  imports: [
    NgIf,
    PlaceBoxComponent,
    FormsModule,
    NgForOf,
    PlaceListSearchComponent,
    PlaceListDetailComponent,
    PlaceNominateBoxComponent,
    PlaceListEmptyComponent,
    PlaceListFilterComponent,
    DatePipe
  ],
  templateUrl: './place-list.component.html',
  styleUrl: './place-list.component.scss'
})
export class PlaceListComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  // User
  currentUser: User | null = null;

  // Region
  isRegionLoading = false;
  isRegionLoaded = false;
  regionSlug: string | null = null
  region: Region | null = null

  // Neighborhood
  neighborhoodSlug: string | null = null

  // Filters
  isFilterGeolocation = false
  isFilterReservation = false
  isFilterAmenities = false

  // Places
  isPlacesLoading = false;
  isPlacesLoaded = false;
  places: Place[] = []

  // Geolocation filter
  latitude: number | null = null
  longitude: number | null = null
  zoom: number | null = null

  // Reservation filter
  reserve: string | null = null
  reservePeople: number | null = null

  // Calculate Reservation
  placeReservableSeats: { [placeId: string]: number } = {}

  // Amenities filter
  amenities: string[] = []

  // Loading
  loading = false

  constructor(private route: ActivatedRoute,
              private placeService: PlaceService,
              private regionService: RegionService,
              private authService: AuthService,
              private filterService: FilterService,
              private mapService: MapService,
              private scheduleService: ScheduleService,
              private generalService: GeneralService) {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser;
    })
  }

  ngOnInit() {
    // Listen to route and query parameter change
    this.loading = true
    combineLatest([this.route.params, this.route.queryParams]).pipe(
      takeUntil(this.onDestroy$),
      map(results => ({params: results[0], queryParams: results[1]})))
      .subscribe(async results => {

        // Filter search
        const latitude = results.queryParams['latitude']
        const longitude = results.queryParams['longitude']
        const reserve = results.queryParams['reserve']
        const reservePeople = results.queryParams['reservePeople']
        const amenities = results.queryParams['amenities']
          ? Array.isArray(results.queryParams['amenities'])
            ? results.queryParams['amenities']
            : [results.queryParams['amenities']]
          : null;

        // Search by geolocation
        if (latitude && longitude) {
          const radius = 100000;

          from(this.refreshPlacesByGeolocation(latitude, longitude, radius))
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(async places => {
              this.isFilterGeolocation = true
              this.latitude = parseFloat(latitude)
              this.longitude = parseFloat(longitude)
              this.zoom = 12

              // Reset region
              this.region = null

              // Map bounds
              const softBound = false; // Not enabled for geolocation because the zoom level is not configured
              const hardBound = true; // Force a hard bound because results may be out of bound

              // Filter places
              let filteredPlaces = places

              // Filter based on amenities
              if (amenities && amenities.length > 0) {
                this.isFilterAmenities = true;
                this.amenities = amenities;

                filteredPlaces = filteredPlaces.filter(place => {
                  let matchingAmenitiesCount = place.amenities.filter(amenity => {
                    return amenities.indexOf(amenity.type) !== -1
                  }).length

                  place.amenities.forEach(amenity => {

                    // Late night or weekend should trigger 24/7
                    if (amenity.type === 'twenty-four-seven' && (amenities.indexOf('late-nights') !== -1 || amenities.indexOf('weekends') !== -1)) {
                      matchingAmenitiesCount++;
                    }

                    // Late night and weekend should trigger 24/7
                    if (amenity.type === 'twenty-four-seven' && (amenities.indexOf('late-nights') !== -1 && amenities.indexOf('weekends') !== -1)) {
                      matchingAmenitiesCount++;
                    }
                  });

                  return amenities.length === matchingAmenitiesCount
                })
              }

              // Filter based reservation
              if (reserve && reservePeople) {
                this.isFilterReservation = true
                this.reserve = reserve
                this.reservePeople = reservePeople

                // Filter based on schedule & seats
                let placeIds = filteredPlaces.map(room => room._id);
                let date = reserve
                let schedules = await firstValueFrom(this.scheduleService.findByPlaceIds(placeIds, date).pipe(takeUntil(this.onDestroy$)))

                filteredPlaces = filteredPlaces.filter(place => {
                  let schedule = schedules.find(schedule => schedule.place && schedule.place._id === place._id);

                  if (schedule && schedule.seats && schedule.seats.length >= reservePeople) {
                    this.placeReservableSeats[place._id] = schedule.seats.length
                    return true;
                  }

                  return false;
                })
              }

              this.places = filteredPlaces

              this.refreshNeighborhood();
              this.refreshMap(softBound, hardBound)

              this.loading = false
            });

          return;
        }

        // Search by region (default)
        this.regionSlug = results.params['regionSlug'];
        this.neighborhoodSlug = results.params['neighborhoodSlug'];

        // Region list
        if (this.regionSlug) {
          this.regionService.regionSlug$.next(this.regionSlug)
        }

        // Using default region slug
        if (this.regionSlug == null) {
          this.regionSlug = await this.regionService.getRegionSlug()
        }

        const regionPromise = this.refreshRegion(this.regionSlug)
        const placesPromise = this.refreshPlaces(this.regionSlug)

        forkJoin([regionPromise, placesPromise]).pipe(takeUntil(this.onDestroy$))
          .subscribe(async results => {
            this.region = results[0]

            // Map bounds
            let softBound = false // Region: false, Neighborhood: true
            let hardBound = false // Not needed since it's not a geolocation search

            // Set region center
            this.latitude = this.region.map!.web!.latitude!
            this.longitude = this.region.map!.web!.longitude!
            this.zoom = this.region.map!.web!.zoom!

            // Filter places
            let places = results[1]
            let filteredPlaces = places

            // Filter based on neighborhoods
            if (this.neighborhoodSlug) {
              filteredPlaces = filteredPlaces.filter(place => {
                let neighborhoodSlug = this.generalService.kebabCase(place.location!.neighborhood!)
                return neighborhoodSlug === this.neighborhoodSlug
              })
            }

            // Filter based on amenities
            if (amenities && amenities.length > 0) {
              this.isFilterAmenities = true;
              this.amenities = amenities;

              filteredPlaces = filteredPlaces.filter(place => {
                let matchingAmenitiesCount = place.amenities.filter(amenity => {
                  return amenities.indexOf(amenity.type) !== -1
                }).length

                place.amenities.forEach(amenity => {

                  // Late night or weekend should trigger 24/7
                  if (amenity.type === 'twenty-four-seven' && (amenities.indexOf('late-nights') !== -1 || amenities.indexOf('weekends') !== -1)) {
                    matchingAmenitiesCount++;
                  }

                  // Late night and weekend should trigger 24/7
                  if (amenity.type === 'twenty-four-seven' && (amenities.indexOf('late-nights') !== -1 && amenities.indexOf('weekends') !== -1)) {
                    matchingAmenitiesCount++;
                  }
                });

                return amenities.length === matchingAmenitiesCount
              })
            }

            // Filter based reservation
            if (reserve && reservePeople) {
              this.isFilterReservation = true
              this.reserve = reserve
              this.reservePeople = reservePeople

              // Filter based on schedule & seats
              let placeIds = filteredPlaces.map(room => room._id);
              let date = reserve
              let schedules = await firstValueFrom(this.scheduleService.findByPlaceIds(placeIds, date).pipe(takeUntil(this.onDestroy$)))

              filteredPlaces = filteredPlaces.filter(place => {
                let schedule = schedules.find(schedule => schedule.place && schedule.place._id === place._id);

                if (schedule && schedule.seats && schedule.seats.length >= reservePeople) {
                  this.placeReservableSeats[place._id] = schedule.seats.length
                  return true;
                }

                return false;
              })
            }

            // No results (zoom into region map)
            if (filteredPlaces.length === 0) {
              softBound = false;
            }

            // Partial results (zoom into bounded results)
            if (filteredPlaces.length > 0 && filteredPlaces.length !== places.length) {
              softBound = true;
            }

            // Full result (zoom into region map)
            if (filteredPlaces.length > 0 && filteredPlaces.length === places.length) {
              softBound = false;
            }

            this.places = filteredPlaces

            this.refreshNeighborhood();
            this.refreshMap(softBound, hardBound)

            this.loading = false
          })
      })
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async refreshRegion(regionSlug: string) {
    this.isRegionLoading = true;
    return firstValueFrom(this.regionService.getBySlug(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRegionLoading = false
        this.isRegionLoaded = true
      })))
  }

  async refreshPlaces(regionSlug: string) {
    this.isPlacesLoading = true;
    return firstValueFrom(this.placeService.findByRegion(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isPlacesLoading = false
        this.isPlacesLoaded = true
      })))
  }

  async refreshPlacesByGeolocation(latitude: number, longitude: number, radius: number) {
    this.isPlacesLoading = true;
    return firstValueFrom(this.placeService.findByGeolocation(latitude, longitude, radius).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isPlacesLoading = false
        this.isPlacesLoaded = true
      })))
  }

  refreshNeighborhood() {
    // Generate all the neighborhoods based on all the places
    const neighborhoods: any[] = [];

    this.places.forEach(place => {
      let isNeighborhoodExist = false;

      // Generic neighborhood ID
      const neighborhoodId = this.generalService.kebabCase(place.location!.regions![0] + ' ' + place.location!.neighborhood!)
      const neighborhoodName = place.location!.neighborhood!
      const neighborhoodSlug = this.generalService.kebabCase(place.location!.neighborhood!)

      // Attempt to search for neighborhoods and update the count
      neighborhoods.forEach(neighborhood => {
        if (neighborhoodId === neighborhood._id) {
          isNeighborhoodExist = true;
          neighborhood.count++;
        }
      });

      // Create a generic neighborhood and add it to the neighborhoods
      if (!isNeighborhoodExist) {
        const neighborhood = {
          _id: neighborhoodId,
          name: neighborhoodName,
          slug: neighborhoodSlug,
          count: 1,
        };
        neighborhoods.push(neighborhood);
      }
    });

    this.filterService.neighborhoods$.next(neighborhoods);
  }

  refreshMap(softBound: boolean, hardBound: boolean) {
    this.mapService.setMapState(this.latitude!, this.longitude!, this.zoom!, this.places.map(place => ({
      latitude: place.location!.latitude!,
      longitude: place.location!.longitude!,
      place: place,
      isOpen: (place.isOpen! || (this.currentUser && this.currentUser.verification && place.isVerifiedOpen)) || false
    })), softBound, hardBound);
  }

  clearFilters() {
    this.filterService.resetFilter();

    this.isFilterGeolocation = false
    this.isFilterReservation = false
    this.isFilterAmenities = false

    this.reserve = null
    this.reservePeople = null
    this.amenities = []

    this.placeReservableSeats = {}
  }

}
